const PREFIX = 'pbkdf2$sha256$';
const ITERATIONS = 100000;
const KEY_LENGTH_BITS = 256;
const SALT_LENGTH_BYTES = 16;
const SALT_HEX_LENGTH = SALT_LENGTH_BYTES * 2;
const HASH_HEX_LENGTH = KEY_LENGTH_BITS / 4;
const PART_COUNT = 5;

const getCrypto = () => window.crypto || window.msCrypto || null;

const requireSubtle = () => {
    const crypto = getCrypto();
    if (!crypto || !crypto.subtle || typeof crypto.subtle.importKey !== 'function') {
        throw new Error(
            'Le hachage des mots de passe hors ligne requiert un contexte securise (HTTPS ou localhost).'
        );
    }
    return crypto;
};

const toHex = (bytes) =>
    Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

const fromHex = (hex) => {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i += 1) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
};

const timingSafeEqual = (a, b) => {
    if (a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) {
        diff |= a[i] ^ b[i];
    }
    return diff === 0;
};

const deriveKey = async (password, salt, iterations) => {
    const crypto = requireSubtle();
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(String(password)),
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
    );
    const bits = await crypto.subtle.deriveBits(
        { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
        keyMaterial,
        KEY_LENGTH_BITS
    );
    return new Uint8Array(bits);
};

export const hashPassword = async (password) => {
    const crypto = requireSubtle();
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH_BYTES));
    const derived = await deriveKey(password, salt, ITERATIONS);
    return `${PREFIX}${ITERATIONS}$${toHex(salt)}$${toHex(derived)}`;
};

export const isHashedPassword = (value) => {
    if (typeof value !== 'string' || !value.startsWith(PREFIX)) return false;
    const parts = value.split('$');
    if (parts.length !== PART_COUNT) return false;
    const iterations = parseInt(parts[2], 10);
    if (!Number.isInteger(iterations) || iterations < 1) return false;
    if (!new RegExp(`^[0-9a-f]{${SALT_HEX_LENGTH}}$`).test(parts[3])) return false;
    if (!new RegExp(`^[0-9a-f]{${HASH_HEX_LENGTH}}$`).test(parts[4])) return false;
    return true;
};

export const verifyPassword = async (password, stored) => {
    if (!isHashedPassword(stored)) return false;
    const parts = stored.split('$');
    const iterations = parseInt(parts[2], 10);
    const derived = await deriveKey(password, fromHex(parts[3]), iterations);
    return timingSafeEqual(derived, fromHex(parts[4]));
};